import { useState } from "react";
import { motion } from "motion/react";
import type { GameKind } from "@minigames/shared";
import { api } from "../lib/api.js";
import { RulesSheet } from "../components/RulesSheet.js";
import { haptic } from "../lib/telegram.js";
import { socket } from "../lib/socket.js";
import { rememberMatch } from "../lib/session.js";
import { useStore } from "../lib/store.js";

const GAME_LABEL: Record<GameKind, { title: string; sub: string; icon: string }> = {
  chess: { title: "Шахматы", sub: "классика 1 на 1", icon: "♞" },
  durak: { title: "Дурак", sub: "подкидной, 36 карт", icon: "🂡" },
  checkers: { title: "Шашки", sub: "русские, бить обязательно", icon: "⛀" },
  reversi: { title: "Реверси", sub: "захвати больше поля", icon: "⚫" },
  gomoku: { title: "Пять в ряд", sub: "быстрая партия", icon: "⁙" },
  seabattle: { title: "Морской бой", sub: "потопи весь флот", icon: "🚢" },
};

export function Lobby() {
  const { me, config, setSnapshot, setInviteLink, setError, enterMatch } = useStore();
  const [game, setGame] = useState<GameKind>("chess");
  const [tc, setTc] = useState("5+3");
  const [busy, setBusy] = useState(false);
  const [opponent, setOpponent] = useState<"friend" | "bot">("friend");
  const [difficulty, setDifficulty] = useState<"easy" | "normal" | "hard">("normal");
  const [rulesOpen, setRulesOpen] = useState(false);

  const controls = config?.timeControls[game] ?? ["5+3"];
  if (!controls.includes(tc)) setTc(controls[0] ?? "5+3");

  async function createMatch() {
    setBusy(true);
    haptic.tap();
    try {
      const res = await api.createMatch(game, tc, opponent, difficulty);
      setInviteLink(res.inviteLink);
      enterMatch(res.matchId);
      setSnapshot(res.snapshot);
      // КРИТИЧНО: подписаться на комнату по сокету. Сервер шлёт обновления
      // только подписчикам — без этого хост не узнает, что соперник вошёл,
      // и навсегда останется на экране ожидания.
      socket.joinMatch(res.matchId);
      rememberMatch(res.matchId);
    } catch (err) {
      setError(err instanceof Error ? err.message : "не удалось создать партию");
    } finally {
      setBusy(false);
    }
  }

  return (
    <motion.div
      className="stack"
      initial={{ opacity: 0, y: 12 }}
      animate={{ opacity: 1, y: 0 }}
      transition={{ duration: 0.28, ease: [0.22, 1, 0.36, 1] }}
    >
      <div className="row" style={{ marginBottom: 4 }}>
        <div>
          <div style={{ fontSize: 22, fontWeight: 700, letterSpacing: "-0.02em" }}>MiniGames</div>
          <div className="muted small">
            {me?.firstName ? `Привет, ${me.firstName}` : "Играй с друзьями"}
          </div>
        </div>
      </div>

      <div className="stack" style={{ gap: 8 }}>
        <div className="muted small">Игра</div>
        {(config?.games ?? (["chess", "durak"] as GameKind[])).map((g) => {
          const info = GAME_LABEL[g];
          const on = g === game;
          return (
            <motion.button
              key={g}
              className="card row"
              style={{
                borderColor: on ? "var(--gold)" : "var(--line)",
                background: on
                  ? "color-mix(in srgb, var(--gold) 10%, var(--bg-elev))"
                  : "var(--bg-elev)",
                textAlign: "left",
              }}
              onClick={() => {
                haptic.select();
                setGame(g);
              }}
              whileTap={{ scale: 0.98 }}
            >
              <span style={{ fontSize: 26, lineHeight: 1 }}>{info.icon}</span>
              <span>
                <span style={{ display: "block", fontWeight: 700 }}>{info.title}</span>
                <span className="muted small">{info.sub}</span>
              </span>
            </motion.button>
          );
        })}
      </div>

      <div className="row" style={{ justifyContent: "space-between" }}>
        <span className="muted small">Не знаешь правила?</span>
        <button
          className="rules-btn"
          onClick={() => {
            haptic.tap();
            setRulesOpen(true);
          }}
        >
          Правила игры
        </button>
      </div>

      <div className="stack" style={{ gap: 8 }}>
        <div className="muted small">Соперник</div>
        <div className="opponent-row">
          <button
            className={`chip${opponent === "friend" ? " on" : ""}`}
            onClick={() => {
              haptic.select();
              setOpponent("friend");
            }}
          >
            Друг
          </button>
          <button
            className={`chip${opponent === "bot" ? " on" : ""}`}
            onClick={() => {
              haptic.select();
              setOpponent("bot");
            }}
          >
            Бот
          </button>
        </div>
        {opponent === "bot" && (
          <div className="chips" style={{ gridTemplateColumns: "repeat(3, 1fr)" }}>
            {(
              [
                ["easy", "Новичок"],
                ["normal", "Уверенный"],
                ["hard", "Сильный"],
              ] as const
            ).map(([value, label]) => (
              <button
                key={value}
                className={`chip${difficulty === value ? " on" : ""}`}
                onClick={() => {
                  haptic.select();
                  setDifficulty(value);
                }}
              >
                {label}
              </button>
            ))}
          </div>
        )}
      </div>

      <div className="stack" style={{ gap: 8 }}>
        <div className="muted small">Контроль времени</div>
        <div className="chips">
          {controls.map((c) => (
            <button
              key={c}
              className={`chip${c === tc ? " on" : ""}`}
              onClick={() => {
                haptic.select();
                setTc(c);
              }}
            >
              {c}
            </button>
          ))}
        </div>
      </div>

      <button className="btn btn-primary" onClick={createMatch} disabled={busy}>
        {busy ? "Создаём…" : opponent === "bot" ? "Играть с ботом" : "Позвать друга"}
      </button>

      <RulesSheet game={game} open={rulesOpen} onClose={() => setRulesOpen(false)} />
    </motion.div>
  );
}
